import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog/dialog"
import { Button } from "../button/button"
import { JobActivityPanel } from "../job-activity/job-activity-panel"
import { type JobActivityStatusVariant } from "../job-activity/job-activity-status"
import { JobActivityStats, type JobActivityStatItem } from "../job-activity/job-activity-stats"
import {
  readTaskFailureCode,
  TASK_FAILURE_REFERENCE_VIDEO_ANALYSIS_FAILED,
  TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_FAILED,
  TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT,
  TASK_FAILURE_REFERENCE_VIDEO_UNAVAILABLE,
} from "./read-task-failure-message"
import { TaskStageChecklist, type TaskStageStatus } from "./task-stage-checklist"
import { Icon } from "../icon/icon"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip/tooltip"
import { cn } from "../../../lib/utils"
import {
  jobActivityPanelAnchorClassName,
  PANEL_EASE,
  panelDockTranslateClassName,
  panelEnterClassName,
  panelHoverClassName,
  panelIconActionClassName,
  panelInlineWidthClassName,
  panelInteractiveClassName,
  panelStaggerDelayClassName,
  panelSurfaceClassName,
} from "../job-activity/job-activity-panel-classes"
import { motion, usePanelDockShift } from "../job-activity/panel-dock"
import { parseTerminalStatusTitle } from "./parse-terminal-status-title"
import { useTranslation } from "react-i18next"
import {
  formatGenerateCountCopy,
  hideProgressBarUntilGenerateCounts,
  readGenerateCountDisplay,
} from "./task-progress-counts"

// ─── Types ──────────────────────────────────────────────────────────

export interface TaskProgressSubtask {
  id: string
  label: string
  /** Completion ratio: 0 = not started, 0–1 = in progress, 1 = done */
  progress: number
  /** Whether this subtask has encountered errors (orthogonal to progress) */
  hasErrors?: boolean
  /** Optional bag — e.g. `{ completedScripts, totalScripts, completedBriefs, totalBriefs }` */
  metadata?: Record<string, unknown>
}

export interface TaskProgressStage {
  id: string
  label: string
  subtasks: TaskProgressSubtask[]
}

export interface TaskProgressData {
  correlationId: string
  type: string
  title: string
  cancellable?: boolean
  stages: TaskProgressStage[]
}

export interface TaskProgressCardLabels {
  view?: string
  dismiss?: string
  retry?: string
}

export interface TaskProgressCardProps {
  task: TaskProgressData
  onDismiss?: () => void
  onCancel?: () => void
  onNavigate?: () => void
  /** Called when the user clicks "Retry" on a failed card. */
  onRetry?: () => void
  isCancelling?: boolean
  /** Tenant name shown at the top of the card. */
  tenantName?: string
  /** Who started the job, shown under the title (already localized). */
  triggeredBy?: string
  /** Render inline (no portal, no fixed positioning) for use inside a sidebar container. */
  inline?: boolean
  /** Localized action labels for terminal cards. */
  labels?: TaskProgressCardLabels
}

// ─── Helpers ────────────────────────────────────────────────────────

const DONE_AUTO_DISMISS_MS = 6_000

type TaskOverallStatus = "active" | "done" | "failed"

function deriveStageStatus(subtasks: TaskProgressSubtask[]): TaskStageStatus {
  const anyErrors = subtasks.some((s) => s.hasErrors)
  if (anyErrors) return "failed"
  const allDone = subtasks.every((s) => s.progress === 1)
  if (allDone) return "done"
  const anyStarted = subtasks.some((s) => s.progress > 0)
  if (anyStarted) return "active"
  return "pending"
}

function deriveOverallStatus(stages: TaskProgressStage[]): TaskOverallStatus {
  const allDone = stages.every((s) => deriveStageStatus(s.subtasks) === "done")
  if (allDone) return "done"
  const anyFailed = stages.some((s) => deriveStageStatus(s.subtasks) === "failed")
  if (anyFailed) return "failed"
  return "active"
}

function getActiveStageIndex(stages: TaskProgressStage[]): number {
  for (let i = stages.length - 1; i >= 0; i--) {
    const status = deriveStageStatus(stages[i].subtasks)
    if (status === "active" || status === "failed") return i
  }
  // All done or all pending — return last done or 0
  for (let i = stages.length - 1; i >= 0; i--) {
    if (deriveStageStatus(stages[i].subtasks) === "done") return i
  }
  return 0
}

// ─── Component ──────────────────────────────────────────────────────

export function TaskProgressCard({
  task,
  onDismiss,
  onCancel,
  onNavigate,
  onRetry,
  isCancelling = false,
  tenantName,
  triggeredBy,
  inline = false,
  labels,
}: TaskProgressCardProps) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = React.useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = React.useState(false)

  const overall = deriveOverallStatus(task.stages)
  const isTerminal = overall === "done" || overall === "failed"

  // Auto-dismiss on completion (only when onDismiss is provided)
  React.useEffect(() => {
    if (overall !== "done") {
      setDismissed(false)
      return
    }
    if (onNavigate) return // Don't auto-dismiss navigable cards
    if (!onDismiss) return // Don't auto-dismiss when sidebar controls visibility
    const id = window.setTimeout(() => setDismissed(true), DONE_AUTO_DISMISS_MS)
    return () => window.clearTimeout(id)
  }, [overall, onNavigate, onDismiss])

  // Close cancel dialog when no longer active
  React.useEffect(() => {
    if (isTerminal) setConfirmCancelOpen(false)
  }, [isTerminal])

  if (dismissed) return null

  // ─── Terminal states ────────────────────────────────────────────

  if (isTerminal) {
    const variant: JobActivityStatusVariant = overall === "done" ? "success" : "error"
    const title =
      overall === "done" ? `${task.title} — Complete` : `${task.title} — Failed`
    const failureCode = overall === "failed" ? readTaskFailureCode(task) : null
    const failureMessage = failureCode ? translateTaskFailure(t, failureCode) : null

    // Done: whole card is a navigate button.
    if (overall === "done" && onNavigate) {
      return (
        <NavigableStatus
          variant={variant}
          title={title}
          onClick={onNavigate}
          onDismiss={onDismiss}
          tenantName={tenantName}
          triggeredBy={triggeredBy}
          inline={inline}
          viewLabel={labels?.view ?? "View"}
          dismissLabel={labels?.dismiss ?? "Dismiss"}
        />
      )
    }

    // Failed: title is optionally navigable; retry button always shown when provided.
    if (overall === "failed" && onRetry) {
      return (
        <RetryableStatus
          title={title}
          failureMessage={failureMessage}
          onRetry={onRetry}
          onDismiss={onDismiss}
          onTitleClick={onNavigate}
          tenantName={tenantName}
          triggeredBy={triggeredBy}
          inline={inline}
          viewLabel={labels?.view ?? "View"}
          dismissLabel={labels?.dismiss ?? "Dismiss"}
          retryLabel={labels?.retry ?? "Retry"}
        />
      )
    }

    if (onNavigate) {
      return (
        <NavigableStatus
          variant={variant}
          title={title}
          failureMessage={failureMessage}
          onClick={onNavigate}
          onDismiss={onDismiss}
          tenantName={tenantName}
          triggeredBy={triggeredBy}
          inline={inline}
          viewLabel={labels?.view ?? "View"}
          dismissLabel={labels?.dismiss ?? "Dismiss"}
        />
      )
    }

    return (
      <TerminalStatusShell
        variant={variant}
        title={title}
        onDismiss={onDismiss}
        tenantName={tenantName}
        triggeredBy={triggeredBy}
        inline={inline}
        dismissLabel={labels?.dismiss ?? "Dismiss"}
      >
        {failureMessage ? <FailureDetail message={failureMessage} /> : null}
      </TerminalStatusShell>
    )
  }

  // ─── Active state ───────────────────────────────────────────────

  const isMultiStage = task.stages.length > 1
  const activeIdx = getActiveStageIndex(task.stages)
  const activeStage = task.stages[activeIdx]

  // Overall task progress (continuous average of all subtask progress values).
  // Persona generate-scripts/briefs match classic Opportunities: hide the
  // footer bar until count metadata exists, then track requested sides so
  // 1 of 5 reads as 20%. Other jobs keep the whole-job average throughout.
  const allSubtasks = task.stages.flatMap((s) => s.subtasks)
  const totalAllSubtasks = allSubtasks.length
  const generateCounts = readGenerateCountDisplay(task.stages)
  const hidePrepBar = hideProgressBarUntilGenerateCounts(task.type, generateCounts != null)
  const countCopy = generateCounts ? formatGenerateCountCopy(generateCounts, t) : null
  const progressSum = allSubtasks.reduce((sum, s) => sum + s.progress, 0)
  const overallPct = hidePrepBar
    ? 0
    : generateCounts
      ? Math.min(100, Math.round((generateCounts.bar.completed / generateCounts.bar.total) * 100))
      : totalAllSubtasks > 0
        ? Math.min(100, Math.round((progressSum / totalAllSubtasks) * 100))
        : 0

  // Active stage subtask progress
  const completedStageSubtasks = activeStage.subtasks.filter((s) => s.progress === 1).length
  const totalStageSubtasks = activeStage.subtasks.length

  const stepLabel = isMultiStage
    ? `Stage ${activeIdx + 1} of ${task.stages.length} — ${activeStage.label}`
    : null

  const panelTitle = isCancelling ? t("taskProgress.cancelling", "Cancelling…") : task.title

  const showCancel = Boolean(onCancel) && task.cancellable && !isCancelling && !isTerminal

  // Stop xor dismiss: a cancellable job the viewer cannot stop (not the owner)
  // still needs a way to hide the card locally.
  const showDismiss = Boolean(onDismiss) && !showCancel

  // Build subtask checklist for the active stage
  const subtaskChecklist = (
    <ol className="flex flex-col gap-1" aria-label="Subtasks">
      {activeStage.subtasks.map((st, i) => (
        <SubtaskRow key={st.id} index={i + 1} label={st.label} progress={st.progress} hasErrors={st.hasErrors} />
      ))}
    </ol>
  )

  const statItems: JobActivityStatItem[] =
    totalAllSubtasks > 0 && !isCancelling && overallPct > 0
      ? [{ id: "pct", label: `${overallPct}%`, emphasis: true }]
      : []

  // When generate counts drive the hero ("1 of 10 scripts"), skip the duplicate subtitle.
  const subtaskCountLabel = generateCounts
    ? null
    : `${completedStageSubtasks} of ${totalStageSubtasks} subtasks`

  return (
    <>
      <JobActivityPanel
        title={panelTitle}
        stepLabel={stepLabel}
        subtitle={subtaskCountLabel}
        completed={generateCounts?.hero.completed}
        total={generateCounts?.hero.total ?? 0}
        totalLabel={countCopy?.totalLabel}
        secondaryLabel={countCopy?.secondaryLabel}
        ariaValueNow={generateCounts?.bar.completed}
        ariaValueMax={generateCounts?.bar.total}
        progressPercent={hidePrepBar || totalAllSubtasks === 0 ? null : overallPct}
        isCancelling={isCancelling}
        segmentSize={0}
        hideProgressBar={totalAllSubtasks === 0 || isCancelling || hidePrepBar}
        stats={statItems.length > 0 ? <JobActivityStats items={statItems} /> : undefined}
        checklist={subtaskChecklist}
        minimizeAriaLabel={t("taskProgress.minimize", "Minimize {{title}}", { title: task.title })}
        expandAriaLabel={t("taskProgress.expand", "Expand {{title}}", { title: task.title })}
        minimizedLabel={generateCounts?.minimizedLabel}
        onStop={showCancel ? () => setConfirmCancelOpen(true) : undefined}
        stopAriaLabel={t("taskProgress.stopTask", "Stop task")}
        stopTitle={t("taskProgress.stopThisTask", "Stop this task")}
        onDismiss={showDismiss ? onDismiss : undefined}
        dismissLabel={labels?.dismiss ?? t("taskProgress.dismiss", "Dismiss")}
        onTitleClick={onNavigate}
        tenantName={tenantName}
        triggeredBy={triggeredBy}
        ariaLabel={`${task.title} progress`}
        ariaValueText={
          generateCounts
            ? (countCopy?.ariaValueText ?? "")
            : hidePrepBar || totalAllSubtasks === 0
              ? "In progress"
              : `${overallPct}% complete`
        }
        disablePortal={inline}
      />

      {showCancel ? (
        <CancelConfirmDialog
          open={confirmCancelOpen}
          onOpenChange={setConfirmCancelOpen}
          onConfirm={() => {
            setConfirmCancelOpen(false)
            onCancel?.()
          }}
          title={task.title}
        />
      ) : null}
    </>
  )
}

// ─── Cancel confirmation dialog ─────────────────────────────────────

function CancelConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  title: string
}) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("taskProgress.stopConfirmTitle", "Stop task?")}</DialogTitle>
          <DialogDescription>
            {t(
              "taskProgress.stopConfirmDescription",
              'Are you sure you want to stop "{{title}}"? This action cannot be undone.',
              { title },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("taskProgress.keepRunning", "Keep running")}
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            {t("taskProgress.stopTask", "Stop task")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Terminal status ────────────────────────────────────────────────

const terminalStatusTone: Record<
  JobActivityStatusVariant,
  { icon: "check-circle" | "alert-circle" | "info"; iconColor: string }
> = {
  neutral: { icon: "info", iconColor: "text-muted-foreground" },
  success: { icon: "check-circle", iconColor: "text-status-success" },
  warning: { icon: "alert-circle", iconColor: "text-status-warning" },
  error: { icon: "alert-circle", iconColor: "text-destructive" },
}

const terminalIconActionClassName = cn(
  // `relative z-10` keeps dismiss above the navigate button's card-wide hit overlay.
  "relative z-10",
  panelIconActionClassName,
)

function terminalStatusIconClass(variant: JobActivityStatusVariant, color: string) {
  return cn(
    "shrink-0 origin-center self-center",
    color,
    variant === "success"
      ? "motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:fade-in motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:animate-none"
      : "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-safe:ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:animate-none",
  )
}

function TerminalMeta({
  parts,
  className,
}: {
  parts: Array<string | undefined>
  className?: string
}) {
  const visible = parts.filter((part): part is string => Boolean(part?.trim()))
  if (visible.length === 0) return null
  return (
    <span className={cn("block truncate text-[11px] leading-normal text-muted-foreground", className)}>
      {visible.join(" · ")}
    </span>
  )
}

function TerminalDismissButton({
  onDismiss,
  label,
}: {
  onDismiss: () => void
  label: string
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onDismiss()
            }}
            aria-label={label}
            className={terminalIconActionClassName}
          >
            <Icon name="x" size="xs" color="inherit" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function TerminalStatusFrame({
  variant,
  inline = false,
  interactive = false,
  children,
}: {
  variant: JobActivityStatusVariant
  inline?: boolean
  interactive?: boolean
  children: React.ReactNode
}) {
  const frameRef = React.useRef<HTMLDivElement>(null)
  // Hook always runs; outside a PanelDock (or when not inline) it yields `"0px"`.
  const dockX = usePanelDockShift(frameRef)

  return (
    <motion.div
      ref={frameRef}
      role="status"
      aria-live="polite"
      data-slot="task-progress-status"
      data-variant={variant}
      style={
        inline
          ? ({ ["--panel-dock-x" as string]: dockX } as React.ComponentProps<typeof motion.div>["style"])
          : undefined
      }
      className={cn(
        !inline && jobActivityPanelAnchorClassName,
        "relative px-3 py-2.5",
        inline ? panelInlineWidthClassName : "w-[22rem]",
        panelSurfaceClassName,
        inline
          ? cn(
              "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150",
              PANEL_EASE,
              panelStaggerDelayClassName,
              "motion-reduce:animate-none",
              variant === "success" && "motion-safe:zoom-in-95",
              panelDockTranslateClassName,
            )
          : panelEnterClassName,
        interactive ? panelInteractiveClassName : panelHoverClassName,
      )}
    >
      {children}
    </motion.div>
  )
}

function TerminalStatusShell({
  variant,
  title,
  tenantName,
  triggeredBy,
  inline = false,
  onDismiss,
  dismissLabel = "Dismiss",
  children,
}: {
  variant: JobActivityStatusVariant
  title: string
  tenantName?: string
  triggeredBy?: string
  inline?: boolean
  onDismiss?: () => void
  dismissLabel?: string
  children?: React.ReactNode
}) {
  const tone = terminalStatusTone[variant]
  const { primary } = parseTerminalStatusTitle(title)

  return (
    <TerminalStatusFrame variant={variant} inline={inline}>
      <div className="flex items-start gap-2">
        <div className="grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] gap-x-2.5">
          <Icon name={tone.icon} size="sm" className={cn("col-start-1 row-start-1", terminalStatusIconClass(variant, tone.iconColor))} aria-hidden />
          <p
            className="col-start-2 row-start-1 min-w-0 self-center truncate text-[13px] font-medium leading-5 tracking-tight text-foreground"
            title={primary}
          >
            {primary}
          </p>
          <TerminalMeta className="col-start-2 row-start-2" parts={[tenantName, triggeredBy]} />
          {children ? <div className="col-start-2 row-start-3">{children}</div> : null}
        </div>
        {onDismiss ? <TerminalDismissButton onDismiss={onDismiss} label={dismissLabel} /> : null}
      </div>
    </TerminalStatusFrame>
  )
}

function translateTaskFailure(
  t: (key: string, defaultValue: string) => string,
  code: string,
): string {
  if (code === TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT) {
    return t(
      "taskProgress.failure.referenceVideoDownloadTimedOut",
      "The reference video took too long to download. Try again, or use a shorter video.",
    )
  }
  if (code === TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_FAILED) {
    return t(
      "taskProgress.failure.referenceVideoDownloadFailed",
      "The reference video could not be downloaded. Check that the post is public, or generate without a video.",
    )
  }
  if (code === TASK_FAILURE_REFERENCE_VIDEO_UNAVAILABLE) {
    return t(
      "taskProgress.failure.referenceVideoUnavailable",
      "This video is missing or private. Try another link, or generate without a video.",
    )
  }
  if (code === TASK_FAILURE_REFERENCE_VIDEO_ANALYSIS_FAILED) {
    return t(
      "taskProgress.failure.referenceVideoAnalysisFailed",
      "The reference video could not be analyzed. Try again, or generate without a video.",
    )
  }
  return t("taskProgress.failure.unknown", "The job failed. Try again.")
}

function FailureDetail({ message, className }: { message: string; className?: string }) {
  return (
    <span className={cn("block text-[12px] leading-4 text-muted-foreground", className)}>{message}</span>
  )
}

function NavigableStatus({
  variant,
  title,
  failureMessage = null,
  onClick,
  onDismiss,
  tenantName,
  triggeredBy,
  inline = false,
  viewLabel,
  dismissLabel,
}: {
  variant: JobActivityStatusVariant
  title: string
  failureMessage?: string | null
  onClick: () => void
  onDismiss?: () => void
  tenantName?: string
  triggeredBy?: string
  inline?: boolean
  viewLabel: string
  dismissLabel: string
}) {
  const tone = terminalStatusTone[variant]
  const { primary, headline, detail } = parseTerminalStatusTitle(title)
  const navigateLabel = `${viewLabel} ${headline}${detail ? `: ${detail}` : ""}`

  return (
    <TerminalStatusFrame variant={variant} inline={inline} interactive>
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onClick}
          aria-label={failureMessage ? `${navigateLabel}. ${failureMessage}` : navigateLabel}
          title={primary}
          className={cn(
            "grid min-w-0 flex-1 cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            // The frame reads as clickable, so stretch the hit area over its padding too.
            "before:absolute before:inset-0 before:rounded-md before:content-['']",
          )}
        >
          <Icon
            name={tone.icon}
            size="sm"
            className={cn("col-start-1 row-start-1", terminalStatusIconClass(variant, tone.iconColor))}
            aria-hidden
          />
          <span className="col-start-2 row-start-1 min-w-0 truncate text-[13px] font-medium leading-5 tracking-tight text-foreground transition-colors duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover/status:text-primary">
            {primary}
          </span>
          <TerminalMeta className="col-start-2 row-start-2" parts={[tenantName, triggeredBy]} />
          {failureMessage ? (
            <FailureDetail message={failureMessage} className="col-start-2 row-start-3" />
          ) : null}
        </button>
        {onDismiss ? <TerminalDismissButton onDismiss={onDismiss} label={dismissLabel} /> : null}
      </div>
    </TerminalStatusFrame>
  )
}

function RetryableStatus({
  title,
  failureMessage = null,
  onRetry,
  onDismiss,
  onTitleClick,
  tenantName,
  triggeredBy,
  inline = false,
  viewLabel,
  dismissLabel,
  retryLabel,
}: {
  title: string
  failureMessage?: string | null
  onRetry: () => void
  onDismiss?: () => void
  onTitleClick?: () => void
  tenantName?: string
  triggeredBy?: string
  inline?: boolean
  viewLabel: string
  dismissLabel: string
  retryLabel: string
}) {
  const { headline, detail } = parseTerminalStatusTitle(title)

  return (
    <TerminalStatusShell
      variant="error"
      title={title}
      tenantName={tenantName}
      triggeredBy={triggeredBy}
      inline={inline}
      onDismiss={onDismiss}
      dismissLabel={dismissLabel}
    >
      {failureMessage ? <FailureDetail message={failureMessage} /> : null}
      <div className="mt-1.5 flex items-center gap-3">
        {onTitleClick ? (
          <button
            type="button"
            onClick={onTitleClick}
            aria-label={`${viewLabel} ${headline}${detail ? `: ${detail}` : ""}`}
            className="text-[12px] font-medium text-foreground/80 transition-colors duration-150 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded-sm"
          >
            {viewLabel}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRetry}
          aria-label={retryLabel}
          className="text-[12px] font-medium text-destructive transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded-sm"
        >
          {retryLabel}
        </button>
      </div>
    </TerminalStatusShell>
  )
}

// ─── Subtask row ────────────────────────────────────────────────────

function SubtaskRow({
  index,
  label,
  progress,
  hasErrors,
}: {
  index: number
  label: string
  progress: number
  hasErrors?: boolean
}) {
  const derivedStatus = hasErrors ? "failed" : progress === 1 ? "done" : progress > 0 ? "active" : "pending"
  return (
    <li
      className={cn(
        "flex items-center gap-2 text-[11px] leading-snug",
        derivedStatus === "pending" && "text-muted-foreground/60",
        derivedStatus === "active" && "font-medium text-foreground",
        derivedStatus === "done" && "text-muted-foreground",
        derivedStatus === "failed" && "text-destructive",
      )}
    >
      <SubtaskIcon progress={progress} hasErrors={hasErrors} />
      <span className="min-w-0 flex-1">
        <span className="mr-1 tabular-nums">{index}.</span>
        {label}
      </span>
      {derivedStatus === "active" && (
        <span className="ml-auto shrink-0 tabular-nums text-[10px] text-muted-foreground">
          {Math.round(progress * 100)}%
        </span>
      )}
    </li>
  )
}

function SubtaskIcon({ progress, hasErrors }: { progress: number; hasErrors?: boolean }) {
  if (hasErrors) {
    return <Icon name="alert-circle" size="xs" className="shrink-0 text-destructive" aria-hidden />
  }
  if (progress === 1) {
    return <Icon name="check-circle" size="xs" className="shrink-0 text-primary" aria-hidden />
  }
  if (progress > 0) {
    return <Icon name="loader" size="xs" className="shrink-0 animate-spin text-primary" aria-hidden />
  }
  return <Icon name="circle" size="xs" className="shrink-0 opacity-40" aria-hidden />
}
